/**
 * The Reports hub route — @intrasync/reports' `reportsRoute` adapted to HQ's
 * conventions, the way `views/dashboard/dashboardRoutesList.ts` adapts the
 * dashboard's. HQ carries no fork: name (`Reports`), path (`/reports`, so
 * `/app/reports`), body, header controls and `beforeEnter` (which mounts the
 * view store) are the library's.
 *
 * It sits in HQ's existing "Reports" nav group beside Developer Metrics
 * (`/app/reports/developer-metrics`). The router matches paths with an
 * anchored regex (`^/reports$`), so neither route shadows the other.
 *
 * WHY EACH OVERRIDE EXISTS:
 *
 * - `requiresPermission: false` — LOAD-BEARING. The library leaves it unset and
 *   @intrasync/stores' `makeRoute` defaults it to TRUE, which demands a GRANT
 *   row for the route's permission path. HQ only ever writes DENY rows (Page
 *   Visibility), so as shipped the route would bounce every user, the CTO
 *   included, to Unauthorized. The real boundary is the api: the reports host
 *   enforces HQ's tiers on every reports-* method.
 * - `requiresAdmin: false` — staff and above see it in the OmniBar, like
 *   Developer Metrics; what each person may do inside is the api's call, and
 *   the hub hides the tabs (Builder, Schedules, Admin) a person cannot use.
 * - `permissionPath: "/app/reports/catalog"` — Page Visibility deny rules match
 *   PARENT paths (`shared/permissionPath.ts` `matchesPermissionPath`). With the
 *   library's `/app/reports`, an admin hiding this hub for someone would also
 *   hide `/app/reports/developer-metrics`. A child path of its own keeps the two
 *   pages independently hideable. (Nothing grants against it: see above.)
 * - `searchAliases` — OmniBar tokens; "reports" itself matches the title.
 *
 * The header is the library's on purpose (spread, not restated): its
 * `component` is `ReportsHeaderControls`, the hub's tab strip rendered in HQ's
 * HubPlate, so the page shows ONE header band; `enableRefreshButton: false`
 * because each tab loads itself.
 *
 * The cast mirrors dashboardRoutesList.ts: HQ's and the library's RouteTypes
 * are deliberately different shapes.
 */
import { reportsRoute } from "@intrasync/reports";
import type { RouteType } from "types/RouteType";

const libraryRoute = reportsRoute as unknown as RouteType;

/** The hub's own Page Visibility path — a child of /app/reports, never its parent. */
export const REPORTS_HUB_PERMISSION_PATH = "/app/reports/catalog";

export const reportsHubRoute: RouteType = {
  ...libraryRoute,
  group: "Reports",
  requiresAdmin: false,
  requiresPermission: false,
  permissionPath: REPORTS_HUB_PERMISSION_PATH,
  searchAliases: [
    "report catalog",
    "run a report",
    "saved views",
    "scheduled reports",
    "export",
    "pdf",
  ],
};
